/* =========================================================================
   The four colleges of Sri Guru Arjun Dev Ji Group of Institutes.

   This file is the single source of truth for programmes across the site —
   the home page register, the /colleges/:slug pages, the admissions form and
   the footer all read from it. Add a course here and it appears everywhere.

   Shape of a college:
     slug        url segment, also the CSS accent key (--v-<slug>)
     name        the college, set in Cinzel caps wherever it appears
     tagline     what the college covers, in plain words
     blurb       one line for the home-page index
     lead        the sentence under the page title
     body        paragraphs for "about this college"
     courses[]   see below
     whyChoose[] optional — reasons to pick this college
     careers[]   optional — what graduates go on to do
     practice[]  optional — { title, items[] } blocks of hands-on training
     awards[]    optional — { title, body } scholarships and awards

   Shape of a course:
     id          unique across the whole site (used as the form value)
     code        the qualification, e.g. "B.Sc" — repeats across courses
     name        full course title, never an invented acronym
     discipline  the part that distinguishes it from its siblings
     duration    human-readable, e.g. "4 years"
     years       number, for the duration bar
     awarding    examination body, where confirmed
     fee         annual fee, where published
     summary     one sentence for the ledger detail
     eligibility entry requirements, verbatim from institute material
     highlights  optional — what the course covers
   ========================================================================= */

import {
  faLeaf,
  faStethoscope,
  faGraduationCap,
  faMicroscope,
  faXRay,
  faUserDoctor,
  faEye,
  faChartLine,
  faLaptopCode,
  faShirt,
  faSpa,
  faScaleBalanced,
  faGavel,
} from "@fortawesome/free-solid-svg-icons";

const SCIENCE_10_2 = [
  "10+2 pass in the Science stream with an aggregate of 50%.",
  "A 5% relaxation in the aggregate applies to SC / ST candidates.",
  "Student shall be medically fit.",
];

export const colleges = [
  /* ------------------------------------------------------- Medical Block */
  {
    slug: "medical",
    name: "Medical Block",
    tagline: "Nursing",
    blurb: "Bedside nursing and midwifery, from a two-year start to a full degree.",
    lead:
      "Our founding college. Three pathways into nursing, each recognised by the councils that let you practise — with clinical postings from the first year.",
    body: [
      "The Medical Block trains student nurses to become efficient members of the health-care team, providing care across varied settings and supported by advanced technology. Teaching runs alongside supervised practice, so students meet patients long before they graduate.",
      "Programmes are recognised by the Indian Nursing Council of Delhi and the Punjab Nurses Registration Council, with the B.Sc degree awarded by Baba Farid University of Health Sciences of Health Sciences.",
    ],
    courses: [
      {
        id: "bsc-nursing",
        code: "B.Sc",
        name: "B.Sc Nursing",
        discipline: "Nursing",
        duration: "4 years",
        years: 4,
        awarding: "Baba Farid University of Health Sciences",
        icon: faGraduationCap,
        summary:
          "A full undergraduate degree blending medical theory, laboratory work and intensive clinical rotations.",
        eligibility: [
          "4 years, usually divided into 8 semesters.",
          "Completion of 10+2 with Physics, Chemistry, Biology (PCB) and English.",
          "10+2 pass in the Science stream with an aggregate of 50%, relaxed by 5% for SC / ST candidates.",
          "Admission is generally based on state-level or university-specific entrance exams rather than NEET.",
        ],
      },
      {
        id: "gnm",
        code: "GNM",
        name: "General Nursing & Midwifery",
        discipline: "General Nursing & Midwifery",
        duration: "3 years",
        years: 3,
        awarding: "Punjab Nurses Registration Council",
        icon: faStethoscope,
        summary:
          "Comprehensive clinical training to practise as a registered nurse and midwife.",
        eligibility: [
          "Minimum and maximum age for admission is 17 and 35 years. There is no age bar for ANM / LHV candidates.",
          "10+2 passed, preferably Science (PCB) & English, with an aggregate of 40% marks.",
          "10+2 in Arts (Mathematics, Biotechnology, Economics, Political Science, History, Geography, Business Studies, Accountancy, Home Science, Sociology, Psychology, Philosophy) & English Core / English Elective, or Health Care Science — Vocational stream only, from a recognised Board under AISSCE / CBSE / ICSE / SSCE / HSCE or equivalent with 40% marks.",
          "10+2 vocational ANM under CBSE or an equivalent board, recognised by the Indian Nursing Council, with 40% marks.",
          "Registered as ANM with the State Nursing Registration Council.",
          "Student shall be medically fit.",
        ],
      },
      {
        id: "anm",
        code: "ANM",
        name: "Auxiliary Nurse Midwifery",
        discipline: "Auxiliary Nurse Midwifery",
        duration: "2 years",
        years: 2,
        awarding: "Punjab Nurses Registration Council",
        icon: faLeaf,
        summary:
          "A grounding in community health and midwifery — the right first step into nursing.",
        eligibility: [
          "Minimum age for admission is 17 years on or before 31st December of the year in which admission is sought.",
          "The maximum age for admission is 35 years.",
          "10+2 in Arts (Maths, Physics, Chemistry, Biology, Biotechnology, Economics, Political Science, History, Geography, Business Studies, Accountancy, Home Science, Sociology, Psychology and Philosophy) & English Core / English Elective, or Science, or Health Care Science — Vocational stream only, from a recognised Board.",
          "Candidates qualified in the 10+2 Arts or Science examination conducted by the National Institute of Open Schooling.",
          "Student shall be medically fit.",
          "Students are admitted once a year.",
        ],
      },
    ],
    careers: [
      "Staff nurse",
      "Registered midwife",
      "Community health nurse",
      "Critical care nurse",
      "Nursing tutor",
      "Public health services",
    ],
  },

  /* --------------------------------------------------- Allied Healthcare */
  {
    slug: "allied-healthcare",
    name: "Allied Healthcare",
    tagline: "Diagnostics, imaging, theatre and eye care",
    blurb:
      "Four B.Sc degrees for the clinical work that happens away from the bedside.",
    lead:
      "The laboratory, the imaging suite, the operation theatre and the eye clinic. Four-year degrees awarded by I.K. Gujral Punjab Technical University, each with supervised hospital postings.",
    body: [
      "Allied healthcare professionals produce the evidence and run the equipment that clinical decisions rest on. These four degrees train students for exactly that work — diagnostic testing, medical imaging, surgical support and primary eye care.",
      "All four programmes share the same entry route: 10+2 in the Science stream with a 50% aggregate, relaxed by 5% for SC / ST candidates, with degrees awarded by I.K. Gujral Punjab Technical University.",
    ],
    courses: [
      {
        id: "bsc-medical-lab-science",
        code: "B.Sc",
        name: "B.Sc Medical Lab Science",
        discipline: "Medical Lab Science",
        duration: "4 years",
        years: 4,
        awarding: "I.K. Gujral Punjab Technical University",
        icon: faMicroscope,
        summary:
          "Train as the diagnostic specialist behind every accurate report, from haematology to microbiology.",
        highlights: [
          "Biochemistry",
          "Pathology",
          "Microbiology",
          "Haematology",
          "Blood banking",
          "Hospital postings",
        ],
        eligibility: SCIENCE_10_2,
      },
      {
        id: "bsc-radiology-and-imaging-technology",
        code: "B.Sc",
        name: "B.Sc Radiology & Imaging Technology",
        discipline: "Radiology & Imaging Technology",
        duration: "4 years",
        years: 4,
        awarding: "I.K. Gujral Punjab Technical University",
        icon: faXRay,
        summary:
          "Learn to operate the imaging technology — X-ray, CT, MRI and ultrasound — that guides modern diagnosis.",
        highlights: [
          "Radiographic technique",
          "Radiation safety",
          "Patient positioning",
          "CT and MRI",
          "Ultrasound",
          "Imaging department postings",
        ],
        eligibility: SCIENCE_10_2,
      },
      {
        id: "bsc-operation-theatre-technology",
        code: "B.Sc",
        name: "B.Sc Operation Theatre Technology",
        discipline: "Operation Theatre Technology",
        duration: "4 years",
        years: 4,
        awarding: "I.K. Gujral Punjab Technical University",
        icon: faUserDoctor,
        summary:
          "Become the technologist the surgical team depends on, from sterilisation to anaesthesia support.",
        highlights: [
          "Sterilisation protocol",
          "Anaesthesia support",
          "Surgical instrumentation",
          "Theatre management",
          "Live theatre postings",
        ],
        eligibility: SCIENCE_10_2,
      },
      {
        id: "bsc-optometry",
        code: "B.Sc",
        name: "B.Sc Optometry",
        discipline: "Optometry",
        duration: "4 years",
        years: 4,
        awarding: "I.K. Gujral Punjab Technical University",
        icon: faEye,
        summary:
          "A primary eye-care degree — vision testing, dispensing and clinical optometry practice.",
        highlights: [
          "Refraction",
          "Contact lens practice",
          "Low-vision care",
          "Ocular diagnostics",
          "Eye-clinic postings",
        ],
        eligibility: SCIENCE_10_2,
      },
    ],
    careers: [
      "Medical laboratory technologist",
      "Radiographer / imaging technologist",
      "Operation theatre technologist",
      "Optometrist",
      "Diagnostic centre supervisor",
      "Equipment application specialist",
    ],
  },

  /* --------------------------------------------------------- Management */
  {
    slug: "management",
    name: "Management",
    tagline: "Business, computing and vocational skills",
    blurb:
      "Degrees in business and computing, plus B.Voc trades built around a skilled craft.",
    lead:
      "Three-year programmes for students heading into business, software and the skilled trades — including two B.Voc courses built around a craft you can practise from day one.",
    body: [
      "The Management block covers the commercial side of the group: a business degree, a computer applications degree, and two vocational degrees that pair classroom teaching with workshop practice.",
      "The B.Voc programmes sit alongside our skill-development work through the Institute of Skill Development and the National Skill Development Corporation, so students finish with a qualification and a trade.",
    ],
    courses: [
      {
        id: "bba",
        code: "BBA",
        name: "Bachelor of Business Administration",
        discipline: "Business Administration",
        duration: "3 years",
        years: 3,
        awarding: "I.K. Gujral Punjab Technical University",
        icon: faChartLine,
        summary:
          "A business degree covering management, marketing, finance and the practice of running an organisation.",
        highlights: [
          "Principles of management",
          "Marketing",
          "Accounting and finance",
          "Human resources",
          "Business communication",
          "Entrepreneurship",
        ],
        eligibility: [
          "10+2 pass from a recognised board in any stream.",
          "A 5% relaxation in the aggregate applies to SC / ST candidates.",
        ],
      },
      {
        id: "bca",
        code: "BCA",
        name: "Bachelor of Computer Applications",
        discipline: "Computer Applications",
        duration: "3 years",
        years: 3,
        awarding: "I.K. Gujral Punjab Technical University",
        icon: faLaptopCode,
        summary:
          "A computing degree covering programming, databases and building software people actually use.",
        highlights: [
          "Programming fundamentals",
          "Data structures",
          "Databases",
          "Web development",
          "Operating systems",
          "Software projects",
        ],
        eligibility: [
          "10+2 pass from a recognised board, with Mathematics or Computer Science preferred.",
          "A 5% relaxation in the aggregate applies to SC / ST candidates.",
        ],
      },
      {
        id: "bvoc-fashion-designer-and-garment-technology",
        code: "B.Voc",
        name: "B.Voc Fashion Designer & Garment Technology",
        discipline: "Fashion Designer & Garment Technology",
        duration: "3 years",
        years: 3,
        awarding: "I.K. Gujral Punjab Technical University",
        icon: faShirt,
        summary:
          "Design, pattern-making and garment construction, taught on the machines students will use in the trade.",
        highlights: [
          "Design fundamentals",
          "Pattern making",
          "Garment construction",
          "Textile science",
          "Surface ornamentation",
          "Studio practice",
        ],
        eligibility: [
          "10+2 pass from a recognised board in any stream.",
          "A 5% relaxation in the aggregate applies to SC / ST candidates.",
        ],
      },
      {
        id: "bvoc-beauty-therapist",
        code: "B.Voc",
        name: "B.Voc Beauty Therapist",
        discipline: "Beauty Therapist",
        duration: "3 years",
        years: 3,
        awarding: "I.K. Gujral Punjab Technical University",
        icon: faSpa,
        summary:
          "Salon and therapy training — skin, hair and client care — with the business skills to run a practice.",
        highlights: [
          "Skin and hair science",
          "Salon practice",
          "Therapy techniques",
          "Client consultation",
          "Hygiene and safety",
          "Salon management",
        ],
        eligibility: [
          "10+2 pass from a recognised board in any stream.",
          "A 5% relaxation in the aggregate applies to SC / ST candidates.",
        ],
      },
    ],
    careers: [
      "Business executive",
      "Software developer",
      "Fashion designer",
      "Garment production supervisor",
      "Beauty therapist",
      "Salon owner",
    ],
  },

  /* ---------------------------------------------------------------- Law */
  {
    slug: "law",
    name: "College of Law",
    tagline: "Legal education",
    blurb:
      "Two routes to practise, taught through moot courts, internships and court visits.",
    lead:
      "Empowering future legal professionals with knowledge, ethics and excellence — a five-year integrated degree for school leavers, and a three-year degree for graduates.",
    body: [
      "Sri Guru Arjun Dev Ji College of Law, Amritsar, is dedicated to providing quality legal education that blends academic excellence with practical legal training. Our mission is to develop knowledgeable, ethical and socially responsible legal professionals capable of meeting the evolving challenges of the legal profession.",
      "The college focuses on experiential learning through moot courts, internships, legal aid activities, workshops, seminars and court visits, enabling students to gain practical exposure beyond the classroom.",
    ],
    vision:
      "To become a leading institution in legal education by nurturing competent professionals committed to justice, integrity and constitutional values.",
    mission: [
      "Deliver quality legal education",
      "Promote professional ethics",
      "Encourage legal research and innovation",
      "Develop advocacy and courtroom skills",
      "Build leadership and communication abilities",
      "Prepare students for successful legal careers",
    ],
    whyChoose: [
      "Affordable fee structure",
      "Experienced faculty",
      "Practical legal education",
      "Moot court training",
      "Legal workshops",
      "Court visits",
      "Internship opportunities",
      "Career guidance",
      "Personality development",
      "Modern learning environment",
    ],
    courses: [
      {
        id: "ba-llb",
        code: "B.A. LL.B.",
        name: "B.A. LL.B.",
        discipline: "Integrated five-year law degree",
        duration: "5 years",
        years: 5,
        fee: "₹60,000 per year",
        icon: faScaleBalanced,
        summary:
          "The integrated five-year programme is designed for students who have completed 10+2 and aspire to build a successful career in the legal profession.",
        highlights: [
          "Constitutional Law",
          "Criminal Law",
          "Civil Law",
          "Corporate Law",
          "Family Law",
          "Cyber Law",
          "Environmental Law",
          "Intellectual Property Rights",
          "Legal Drafting",
          "Advocacy Skills",
          "Moot Court Exercises",
          "Legal Research",
        ],
        eligibility: [
          "10+2 pass from a recognised board.",
          "Annual fee: ₹60,000 per year.",
          "SC / ST students may avail scholarship benefits as per Government norms.",
        ],
      },
      {
        id: "llb",
        code: "LL.B.",
        name: "LL.B.",
        discipline: "Three-year professional law degree",
        duration: "3 years",
        years: 3,
        fee: "₹42,000 per year",
        icon: faGavel,
        summary:
          "The three-year professional programme is designed for graduates seeking a career in law.",
        highlights: [
          "Litigation Training",
          "Professional Ethics",
          "Trial Advocacy",
          "Drafting & Pleading",
          "Legal Research",
          "Court Visits",
          "Practical Case Studies",
          "Moot Court Competitions",
          "Internship Programmes",
        ],
        eligibility: [
          "A bachelor's degree from a recognised university.",
          "Annual fee: ₹42,000 per year.",
          "SC / ST students may avail scholarship benefits as per Government norms.",
        ],
      },
    ],
    practice: [
      {
        title: "Moot court training",
        note:
          "Students regularly participate in simulated courtroom proceedings to develop advocacy, argumentation, drafting and presentation skills.",
        items: [],
      },
      {
        title: "Internship opportunities",
        note: "The institute facilitates internships with:",
        items: [
          "District Courts",
          "High Courts",
          "Law firms",
          "Senior advocates",
          "Corporate legal departments",
          "Government offices",
          "NGOs",
        ],
      },
      {
        title: "Legal workshops",
        note:
          "Sessions are delivered by experienced advocates, judges, academicians and legal experts on:",
        items: [
          "Constitutional Law",
          "Criminal Justice",
          "Cyber Law",
          "Intellectual Property Rights",
          "Consumer Protection",
          "Corporate Law",
          "Arbitration & Mediation",
          "Legal Drafting",
        ],
      },
      {
        title: "Additional learning activities",
        note: "",
        items: [
          "Court visits",
          "Legal aid camps",
          "Client counselling",
          "Debate competitions",
          "Judgment analysis",
          "Research projects",
          "Personality development",
          "Soft skills training",
        ],
      },
    ],
    awards: [
      {
        title: "SC / ST scholarship",
        body: "Eligible students may avail scholarship benefits as per Government norms.",
      },
      {
        title: "Merit scholarship",
        body: "Students securing 85% or above in Class 12 are eligible for a merit scholarship.",
      },
    ],
    careers: [
      "Advocate",
      "Judicial services",
      "Corporate lawyer",
      "Legal advisor",
      "Public prosecutor",
      "Legal consultant",
      "Civil services",
      "Banking & insurance legal officer",
      "Human rights organisations",
      "Academia & research",
    ],
  },
];

/** Every course on the site, each tagged with the college it belongs to. */
export const allCourses = colleges.flatMap((c) =>
  c.courses.map((course) => ({ ...course, college: c.name, collegeSlug: c.slug }))
);

export const findCollege = (slug) => colleges.find((c) => c.slug === slug);

export const courseCount = allCourses.length;
